import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface ContextInfo {
    estimatedTotalTokens: number;
    maxContextTokens: number;
    remainingTokens: number;
    percentageUsed: number;
    warning?: string;
}

export interface AttachmentData {
    fileName: string;
    tokens: number;
    size: number;
    pages: number;
    content: string;
    contextInfo?: ContextInfo;
}

export interface AttachmentState {
    // Map of chatId -> list of attachment data
    attachments: Record<string, AttachmentData[]>;
    // Map of chatId -> upload status
    isUploading: Record<string, boolean>;
    // Map of chatId -> error message
    uploadErrors: Record<string, string | null>;
    // Map of chatId -> context warnings
    contextWarnings: Record<string, string | null>;
}

const initialState: AttachmentState = {
    attachments: {},
    isUploading: {},
    uploadErrors: {},
    contextWarnings: {},
};

const chatAttachmentSlice = createSlice({
    name: 'chatAttachment',
    initialState,
    reducers: {
        addAttachment: (state, action: PayloadAction<{ chatId: string; attachment: AttachmentData }>) => {
            const { chatId, attachment } = action.payload;
            if (!state.attachments[chatId]) {
                state.attachments[chatId] = [];
            }
            state.attachments[chatId].push(attachment);
            state.uploadErrors[chatId] = null;
            // Set context warning if present (latest wins)
            if (attachment.contextInfo?.warning) {
                state.contextWarnings[chatId] = attachment.contextInfo.warning;
            } else {
                state.contextWarnings[chatId] = null;
            }
        },
        removeAttachment: (state, action: PayloadAction<{ chatId: string; index: number }>) => {
            const { chatId, index } = action.payload;
            if (state.attachments[chatId]) {
                state.attachments[chatId].splice(index, 1);
            }
            state.contextWarnings[chatId] = null; // Clear warning when removing (simplification)
        },
        clearAttachments: (state, action: PayloadAction<string>) => {
            const chatId = action.payload;
            state.attachments[chatId] = [];
            state.uploadErrors[chatId] = null;
            state.contextWarnings[chatId] = null;
        },
        setUploading: (state, action: PayloadAction<{ chatId: string; isUploading: boolean }>) => {
            const { chatId, isUploading } = action.payload;
            state.isUploading[chatId] = isUploading;
        },
        setUploadError: (state, action: PayloadAction<{ chatId: string; error: string }>) => {
            const { chatId, error } = action.payload;
            state.uploadErrors[chatId] = error;
            state.isUploading[chatId] = false;
        },
        clearUploadError: (state, action: PayloadAction<string>) => {
            const chatId = action.payload;
            state.uploadErrors[chatId] = null;
        },
    },
});

export const {
    addAttachment,
    removeAttachment,
    clearAttachments,
    setUploading,
    setUploadError,
    clearUploadError,
} = chatAttachmentSlice.actions;

export default chatAttachmentSlice.reducer;
